import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Tag, MapPin, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TID } from "@/constants/testIds";

const colorMap = {
    olive: "bg-caireen-olive text-white",
    yellow: "bg-caireen-yellow text-caireen-ink",
    blue: "bg-caireen-blue text-white",
    orange: "bg-caireen-orange text-white",
    red: "bg-caireen-red text-white",
};

const categoryKeys = ["all", "health", "education", "research", "social", "culture", "employment"];

export default function Projects() {
    const { t, lang } = useLang();
    const p = t.projectsPage;
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        let active = true;
        api.getProjects()
            .then((d) => active && setProjects(d.items || []))
            .catch(() => active && setProjects([]))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, []);

    const filtered = useMemo(() => {
        if (filter === "all") return projects;
        return projects.filter((pr) => pr.category === filter);
    }, [projects, filter]);

    return (
        <div data-testid="page-projects" className="px-6 md:px-10 py-12 md:py-20">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl"
                >
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-caireen-blue/10 text-caireen-blue font-sans text-xs tracking-widest uppercase">
                        <Tag className="w-3.5 h-3.5" />
                        {t.nav.projects}
                    </span>
                    <h1 className="mt-5 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">{p.title}</h1>
                    <p className="mt-5 font-sans text-lg text-caireen-ink/70">{p.sub}</p>
                </motion.div>

                {/* Filters */}
                <div className="mt-10 flex flex-wrap gap-2" data-testid="project-filters">
                    {categoryKeys.map((k) => (
                        <button
                            key={k}
                            data-testid={`filter-${k}`}
                            onClick={() => setFilter(k)}
                            className={`px-4 py-2 rounded-full font-sans text-sm font-medium transition-all ${
                                filter === k
                                    ? "bg-caireen-ink text-caireen-cream"
                                    : "bg-white text-caireen-ink/70 ring-1 ring-caireen-ink/10 hover:ring-caireen-ink/30"
                            }`}
                        >
                            {p.filter[k]}
                        </button>
                    ))}
                </div>

                {/* Grid */}
                {loading ? (
                    <div className="mt-16 flex items-center justify-center text-caireen-ink/40">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
                        {filtered.map((pr, i) => (
                            <motion.article
                                key={pr.id}
                                data-testid={TID.projectCard(pr.slug)}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                whileHover={{ y: -6 }}
                                className="group bg-white rounded-3xl overflow-hidden ring-1 ring-caireen-ink/10 hover:shadow-[0_24px_60px_-24px_rgba(31,41,55,0.18)] transition-all"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img
                                        src={pr.image}
                                        alt={pr.title[lang] || pr.title.de}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <span className={`absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-sans text-xs uppercase tracking-wider ${colorMap[pr.color] || colorMap.orange}`}>
                                        {p.filter[pr.category] || pr.category}
                                    </span>
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center gap-2 font-sans text-xs uppercase tracking-widest text-caireen-ink/50">
                                        <span>{pr.year}</span>
                                        <span>·</span>
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {pr.country}
                                        </span>
                                    </div>
                                    <h3 className="mt-3 font-serif text-2xl leading-tight tracking-tight">
                                        {pr.title[lang] || pr.title.de}
                                    </h3>
                                    <p className="mt-2.5 font-sans text-sm text-caireen-ink/65 leading-relaxed">
                                        {pr.summary[lang] || pr.summary.de}
                                    </p>
                                    <div className="mt-5 inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-caireen-orange group-hover:text-caireen-red transition-colors">
                                        {p.readMore}
                                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
